import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, DOXICO_LOGO } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowRight, KeyRound, Mail, Shield } from "lucide-react";

const STUDIO_BG =
  "https://static.prod-images.emergentagent.com/jobs/0fc8b43f-183d-4fa7-83a3-b053819142c5/images/04a1341d1c6c66f2f57b09068afcb2920d2e697a604ed9268f1005879113e79d.png";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("reset_token") ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetToken, setResetToken] = useState(params.get("reset_token") || "");
  const [newPw, setNewPw] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("reset_token")) setMode("reset");
  }, [params]);

  const submitLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password, totp_code: totp || null });
      if (r.data.requires_totp) {
        setNeedsTotp(true);
        toast.info("Enter your 6-digit authenticator code");
        return;
      }
      setUser(r.data.user);
      if (r.data.user.must_change_password) {
        navigate("/change-password", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitResetRequest = async (e) => {
    e?.preventDefault();
    if (!resetEmail) return;
    setLoading(true);
    try {
      await api.post("/auth/reset/request", { email: resetEmail });
      toast.success("If that email exists, a reset link has been sent to Admin.");
      setMode("login");
    } catch (err) {
      toast.error("Failed");
    } finally {
      setLoading(false);
    }
  };

  const submitResetConfirm = async (e) => {
    e?.preventDefault();
    if (!resetToken || newPw.length < 8) {
      toast.error("Password must be 8+ characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset/confirm", { token: resetToken, new_password: newPw });
      toast.success("Password reset. Please log in.");
      setMode("login");
      setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative overflow-hidden" data-testid="login-page">
      <img src={STUDIO_BG} alt="Studio" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/75" />
      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between p-10">
          <img src={DOXICO_LOGO} alt="Doxico" className="h-12 object-contain object-left" />
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-4">
              Shoot · Edit · Approve · Publish
            </div>
            <h1 className="font-heading text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight text-white max-w-xl">
              Welcome to your studio's control room.
            </h1>
            <p className="mt-6 text-neutral-300 max-w-md leading-relaxed">
              Sign in with the credentials your admin gave you. Set up two-factor auth from your profile for extra peace of mind.
            </p>
          </div>
          <div className="text-xs text-neutral-600">© Doxico Studio · Production Suite</div>
        </div>

        <div className="flex items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-md bg-neutral-950/85 backdrop-blur-xl border border-neutral-800 p-8 md:p-10" data-testid="login-card">
            <div className="lg:hidden mb-8">
              <img src={DOXICO_LOGO} alt="Doxico" className="h-10 object-contain object-left" />
            </div>

            {mode === "login" && (
              <form onSubmit={submitLogin} className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Sign in</div>
                  <h2 className="font-heading text-3xl font-semibold text-white tracking-tight">
                    Hi there 👋
                  </h2>
                  <p className="text-sm text-neutral-400 mt-2">Use your Doxico work email & password.</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@doxico.studio"
                      data-testid="login-email"
                      className="pl-9 rounded-none bg-neutral-900 border-neutral-800 h-11"
                      required
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Password</Label>
                    <button type="button" onClick={() => setMode("request")} className="text-[10px] uppercase tracking-[0.2em] text-[#00C97A] hover:text-emerald-300">Forgot?</button>
                  </div>
                  <div className="relative mt-2">
                    <KeyRound className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password"
                      className="pl-9 rounded-none bg-neutral-900 border-neutral-800 h-11"
                      required
                    />
                  </div>
                </div>
                {needsTotp && (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Authenticator Code</Label>
                    <div className="relative mt-2">
                      <Shield className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                      <Input
                        value={totp}
                        onChange={(e) => setTotp(e.target.value)}
                        placeholder="123 456"
                        autoFocus
                        inputMode="numeric"
                        maxLength={6}
                        data-testid="login-totp"
                        className="pl-9 rounded-none bg-neutral-900 border-neutral-800 h-11 tracking-[0.5em] text-center"
                      />
                    </div>
                  </div>
                )}
                <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-11 rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black font-medium">
                  {loading ? "Signing in…" : "Sign in"}
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
                <div className="text-[10px] text-neutral-600 text-center pt-2">
                  Default admin (first run): <span className="font-mono text-neutral-500">admin@doxico.local</span> / <span className="font-mono text-neutral-500">Doxico@2026</span>
                </div>
              </form>
            )}

            {mode === "request" && (
              <form onSubmit={submitResetRequest} className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Reset password</div>
                  <h2 className="font-heading text-3xl font-semibold text-white tracking-tight">Forgot your key?</h2>
                  <p className="text-sm text-neutral-400 mt-2">Your admin will see a reset link in their inbox and share it with you.</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Your email</Label>
                  <Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} data-testid="reset-email" className="mt-2 rounded-none bg-neutral-900 border-neutral-800 h-11" required />
                </div>
                <Button type="submit" disabled={loading} data-testid="reset-request-btn" className="w-full h-11 rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">
                  Send reset request
                </Button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-white pt-2">← Back to sign in</button>
              </form>
            )}

            {mode === "reset" && (
              <form onSubmit={submitResetConfirm} className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Choose new password</div>
                  <h2 className="font-heading text-3xl font-semibold text-white tracking-tight">Set a fresh key 🔐</h2>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Reset token</Label>
                  <Input value={resetToken} onChange={(e) => setResetToken(e.target.value)} data-testid="reset-token" className="mt-2 rounded-none bg-neutral-900 border-neutral-800 h-11 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">New password (8+ chars)</Label>
                  <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-new-password" className="mt-2 rounded-none bg-neutral-900 border-neutral-800 h-11" />
                </div>
                <Button type="submit" disabled={loading} data-testid="reset-confirm-btn" className="w-full h-11 rounded-none bg-[#00C97A] hover:bg-[#00B26B] text-black">
                  Update password
                </Button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-white pt-2">← Back to sign in</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
